import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Image as ImageIcon,
  Clock,
  MessageCircle,
  Eye,
  BookOpen,
  ArrowRight,
} from "lucide-react";
import { useAuth } from "../../services/AuthContext.js";
import { getAllPosts, deletePost } from "../../services/user_posts.js";
import { getLatestComments } from "../../services/comments.js";
import { usePostViewTracker } from "../../services/usePostViewTracker.js";
import CreatePost from "../account/create_post.js";
import EditPostModal from "../account/edit_post.js";
import OwnerActionsMenu from "../../components/OwnerActionsMenu.js";
import ConfirmDialog from "../../components/ConfirmDialog.js";
import "./feed.css";

const PAGE_SIZE = 20;

const default_profile_image_url =
  "https://dominio-eletrico-profile-images-dev.s3.us-east-1.amazonaws.com/default_user_image.jpg";

const formatDateTime = (iso) => {
  try {
    return new Intl.DateTimeFormat("pt-BR", { dateStyle: "medium" }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const byCreatedAtDesc = (a, b) => {
  const ta = new Date(a.created_at || 0).getTime();
  const tb = new Date(b.created_at || 0).getTime();
  return tb - ta;
};

const AuthorLine = ({ author = {} }) => {
  const [imgError, setImgError] = useState(false);
  const name = author.full_name || "Usuário";
  const photoSrc = author.profile_photo && !imgError ? author.profile_photo : default_profile_image_url;

  const avatar = (
    <img
      src={photoSrc}
      alt={`Foto de ${name}`}
      className="feed-post-avatar"
      onError={() => setImgError(true)}
    />
  );

  return (
    <div className="feed-post-author">
      {author.slug ? (
        <Link
          to={`/user/${author.slug}`}
          className="feed-post-author-link"
          onClick={(e) => e.stopPropagation()}
        >
          {avatar}
          <span className="feed-post-author-name">{name}</span>
        </Link>
      ) : (
        <span className="feed-post-author-link">
          {avatar}
          <span className="feed-post-author-name">{name}</span>
        </span>
      )}
    </div>
  );
};

const LatestComment = ({ comment }) => {
  const [imgError, setImgError] = useState(false);
  const author = comment.author || {};
  const name = author.full_name || "Usuário";
  const photoSrc = author.profile_photo && !imgError ? author.profile_photo : default_profile_image_url;

  return (
    <div className="feed-comment">
      {author.slug ? (
        <Link to={`/user/${author.slug}`} className="feed-comment-avatar-link">
          <img
            src={photoSrc}
            alt={`Foto de ${name}`}
            className="feed-comment-avatar"
            onError={() => setImgError(true)}
          />
        </Link>
      ) : (
        <img
          src={photoSrc}
          alt={`Foto de ${name}`}
          className="feed-comment-avatar"
          onError={() => setImgError(true)}
        />
      )}
      <div className="feed-comment-body">
        <div className="feed-comment-header">
          {author.slug ? (
            <Link to={`/user/${author.slug}`} className="feed-comment-author">
              {name}
            </Link>
          ) : (
            <span className="feed-comment-author">{name}</span>
          )}
          <span className="feed-comment-date">{formatDateTime(comment.created_at)}</span>
        </div>
        <p className="feed-comment-text">{comment.content}</p>
      </div>
    </div>
  );
};

const FeedPostCard = ({ post, currentUserId, onEdit, onRequestDelete, actionsDisabled }) => {
  const images = post.image_urls || [];
  const [imgSrc, setImgSrc] = useState(images[0]);
  const commentsCount = post.comments_count || 0;
  const viewsCount = Number(post.views_count) || 0;
  const latestComment = post.latest_comment;
  const moreComments = Math.max(commentsCount - (latestComment ? 1 : 0), 0);
  const viewRef = usePostViewTracker(post?.id);
  const isOwner = Boolean(currentUserId && post.author_id === currentUserId);

  useEffect(() => {
    setImgSrc(images[0]);
  }, [images[0]]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <article className="feed-post" ref={viewRef}>
      <div className="feed-post-top">
        <AuthorLine author={post.author || {}} />
        {isOwner && (
          <OwnerActionsMenu
            disabled={actionsDisabled}
            onEdit={() => onEdit?.(post)}
            onDelete={() => onRequestDelete?.(post)}
          />
        )}
      </div>

      <Link to={`/post/${post.id}`} className="feed-post-media">
        {imgSrc ? (
          <img
            src={imgSrc}
            alt="Imagem da publicação"
            className="feed-post-media-img"
            onError={() => setImgSrc(null)}
          />
        ) : (
          <div className="feed-post-media-fallback">
            <ImageIcon size={30} />
          </div>
        )}
        {images.length > 1 && (
          <span className="feed-post-media-count">
            <ImageIcon size={12} /> {images.length}
          </span>
        )}
      </Link>

      <div className="feed-post-body">
        <Link to={`/post/${post.id}`} className="feed-post-title-link">
          <h3 className="feed-post-title">{post.title}</h3>
        </Link>
        <p className="feed-post-desc">{post.description}</p>
        <div className="feed-post-meta">
          <span>
            <Clock size={14} /> {formatDateTime(post.created_at)}
          </span>
          <span>
            <Eye size={14} />{" "}
            {viewsCount === 1 ? "1 visualização" : `${viewsCount} visualizações`}
          </span>
          <span>
            <MessageCircle size={14} />{" "}
            {commentsCount === 1 ? "1 comentário" : `${commentsCount} comentários`}
          </span>
        </div>
      </div>

      {latestComment && (
        <div className="feed-post-comments">
          <LatestComment comment={latestComment} />
          {moreComments > 0 && (
            <Link to={`/post/${post.id}`} className="feed-more-comments">
              {moreComments === 1
                ? "Ver mais 1 comentário"
                : `Ver mais ${moreComments} comentários`}
            </Link>
          )}
        </div>
      )}
    </article>
  );
};

const FeedLessonCommentCard = ({ comment }) => {
  const user = comment.user || {};
  const lesson = comment.lesson || {};
  const name = user.full_name || user.slug || "Usuário";
  const lessonHref = lesson.link || null;
  const lessonLabel =
    [lesson.course_title, lesson.section_title, lesson.lesson_title]
      .filter(Boolean)
      .join(" · ") ||
    lesson.lesson_title ||
    "Aula";

  return (
    <article className="feed-post feed-lesson-comment">
      <div className="feed-lesson-comment-badge">
        <MessageCircle size={14} />
        Comentário em aula
      </div>

      <AuthorLine
        author={{
          full_name: name,
          slug: user.slug,
          profile_photo: user.profile_photo,
        }}
      />

      <div className="feed-post-body">
        <p className="feed-lesson-comment-text">{comment.content}</p>

        {lessonHref ? (
          <Link to={lessonHref} className="feed-lesson-link">
            <BookOpen size={16} />
            <span className="feed-lesson-link-text">
              <strong>Aula:</strong> {lessonLabel}
            </span>
            <ArrowRight size={16} className="feed-lesson-link-arrow" />
          </Link>
        ) : (
          <div className="feed-lesson-link feed-lesson-link--static">
            <BookOpen size={16} />
            <span className="feed-lesson-link-text">
              <strong>Aula:</strong> {lessonLabel}
            </span>
          </div>
        )}

        <div className="feed-post-meta">
          <span>
            <Clock size={14} /> {formatDateTime(comment.created_at)}
          </span>
          {user.slug && (
            <Link to={`/user/${user.slug}`} className="feed-lesson-profile-link">
              Ver perfil
            </Link>
          )}
        </div>
      </div>
    </article>
  );
};

function commentItemKey(comment) {
  return [
    "lesson-comment",
    comment.lesson_key || "",
    comment.created_at || "",
    comment.user?.user_id || "",
    (comment.content || "").slice(0, 24),
  ].join("|");
}

function Feed() {
  const { user, getIdToken } = useAuth();
  const currentUserId = user?.cognitoUser?.username || "";
  const [filter, setFilter] = useState("all"); // all | posts | comments
  const [posts, setPosts] = useState([]);
  const [lessonComments, setLessonComments] = useState([]);
  const [postsOffset, setPostsOffset] = useState(0);
  const [commentsOffset, setCommentsOffset] = useState(0);
  const [postsHasMore, setPostsHasMore] = useState(true);
  const [commentsHasMore, setCommentsHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [postPendingDelete, setPostPendingDelete] = useState(null);
  const [deletingPost, setDeletingPost] = useState(false);

  const fetchPostsPage = useCallback(
    async (currentOffset, { append }) => {
      const token = await getIdToken();
      const data = await getAllPosts(token, { offset: currentOffset, limit: PAGE_SIZE });
      const batch = data?.posts || [];
      const moreAvailable =
        typeof data?.has_next === "boolean" ? data.has_next : batch.length === PAGE_SIZE;

      setPosts((prev) => {
        if (!append) return batch;
        const existingIds = new Set(prev.map((p) => p.id));
        return [...prev, ...batch.filter((p) => !existingIds.has(p.id))];
      });
      setPostsOffset(currentOffset + batch.length);
      setPostsHasMore(moreAvailable && batch.length > 0);
    },
    [getIdToken]
  );

  const fetchCommentsPage = useCallback(
    async (currentOffset, { append }) => {
      const token = await getIdToken();
      const data = await getLatestComments(token, { offset: currentOffset, limit: PAGE_SIZE });
      const batch = data?.comments || [];
      const moreAvailable =
        typeof data?.has_next === "boolean" ? data.has_next : batch.length === PAGE_SIZE;

      setLessonComments((prev) => {
        if (!append) return batch;
        const existing = new Set(prev.map(commentItemKey));
        return [...prev, ...batch.filter((c) => !existing.has(commentItemKey(c)))];
      });
      setCommentsOffset(currentOffset + batch.length);
      setCommentsHasMore(moreAvailable && batch.length > 0);
    },
    [getIdToken]
  );

  const loadInitial = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setPosts([]);
      setLessonComments([]);
      setPostsOffset(0);
      setCommentsOffset(0);
      setPostsHasMore(true);
      setCommentsHasMore(true);

      if (filter === "posts") {
        await fetchPostsPage(0, { append: false });
        setCommentsHasMore(false);
      } else if (filter === "comments") {
        await fetchCommentsPage(0, { append: false });
        setPostsHasMore(false);
      } else {
        await Promise.all([
          fetchPostsPage(0, { append: false }),
          fetchCommentsPage(0, { append: false }),
        ]);
      }
    } catch (err) {
      console.error("Erro ao carregar o feed:", err);
      setError("Não foi possível carregar o feed.");
    } finally {
      setLoading(false);
    }
  }, [filter, fetchPostsPage, fetchCommentsPage]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const loadMore = async () => {
    try {
      setLoading(true);
      setError(null);
      if (filter === "posts") {
        await fetchPostsPage(postsOffset, { append: true });
      } else if (filter === "comments") {
        await fetchCommentsPage(commentsOffset, { append: true });
      } else {
        const tasks = [];
        if (postsHasMore) tasks.push(fetchPostsPage(postsOffset, { append: true }));
        if (commentsHasMore) tasks.push(fetchCommentsPage(commentsOffset, { append: true }));
        await Promise.all(tasks);
      }
    } catch (err) {
      console.error("Erro ao carregar mais itens:", err);
      setError("Não foi possível carregar mais itens.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = (post) => {
    setPosts((prev) => [post, ...prev]);
    if (filter === "comments") setFilter("all");
  };

  const handlePostUpdated = (updated) => {
    setPosts((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
    setEditingPost(null);
  };

  const handleConfirmDeletePost = async () => {
    if (!postPendingDelete || deletingPost) return;
    const postId = postPendingDelete.id;
    try {
      setDeletingPost(true);
      const token = await getIdToken();
      await deletePost(token, postId);
      setPosts((prev) => prev.filter((p) => p.id !== postId));
      setPostPendingDelete(null);
    } catch (err) {
      console.error("Erro ao excluir publicação:", err);
      if (err.status === 403) {
        setPostPendingDelete(null);
        setError("Você não tem permissão para excluir esta publicação.");
      } else if (err.status === 404) {
        setPosts((prev) => prev.filter((p) => p.id !== postId));
        setPostPendingDelete(null);
      } else {
        setError(err.message || "Não foi possível excluir a publicação.");
        setPostPendingDelete(null);
      }
    } finally {
      setDeletingPost(false);
    }
  };

  const feedItems = useMemo(() => {
    const items = [];
    if (filter !== "comments") {
      posts.forEach((post) => {
        items.push({ kind: "post", created_at: post.created_at, post });
      });
    }
    if (filter !== "posts") {
      lessonComments.forEach((comment) => {
        items.push({ kind: "lesson_comment", created_at: comment.created_at, comment });
      });
    }
    return items.sort(byCreatedAtDesc);
  }, [filter, posts, lessonComments]);

  const hasMore =
    filter === "posts"
      ? postsHasMore
      : filter === "comments"
        ? commentsHasMore
        : postsHasMore || commentsHasMore;

  const emptyMessage =
    filter === "posts"
      ? "Ainda não há publicações."
      : filter === "comments"
        ? "Ainda não há comentários em aulas."
        : "Ainda não há atividade no feed.";

  return (
    <div className="feed">
      <h1 className="feed-title">Feed</h1>

      <div className="feed-filter" role="tablist" aria-label="Filtrar feed">
        <button
          type="button"
          role="tab"
          aria-selected={filter === "all"}
          className={`feed-filter-btn${filter === "all" ? " active" : ""}`}
          onClick={() => setFilter("all")}
        >
          Tudo
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={filter === "posts"}
          className={`feed-filter-btn${filter === "posts" ? " active" : ""}`}
          onClick={() => setFilter("posts")}
        >
          Publicações
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={filter === "comments"}
          className={`feed-filter-btn${filter === "comments" ? " active" : ""}`}
          onClick={() => setFilter("comments")}
        >
          Comentários de aulas
        </button>
      </div>

      {filter !== "comments" && <CreatePost onCreate={handleCreatePost} />}

      {error && <div className="feed-error">{error}</div>}

      <div className="feed-list">
        {feedItems.map((item) =>
          item.kind === "post" ? (
            <FeedPostCard
              key={`post-${item.post.id}`}
              post={item.post}
              currentUserId={currentUserId}
              onEdit={setEditingPost}
              onRequestDelete={setPostPendingDelete}
              actionsDisabled={deletingPost}
            />
          ) : (
            <FeedLessonCommentCard
              key={commentItemKey(item.comment)}
              comment={item.comment}
            />
          )
        )}
      </div>

      {loading && <p className="feed-empty">Carregando...</p>}

      {!loading && feedItems.length === 0 && !error && (
        <p className="feed-empty">{emptyMessage}</p>
      )}

      {!loading && hasMore && feedItems.length > 0 && (
        <div className="feed-load-more-wrapper">
          <button type="button" className="feed-load-more" onClick={loadMore}>
            Carregar mais
          </button>
        </div>
      )}

      <EditPostModal
        post={editingPost}
        open={Boolean(editingPost)}
        onClose={() => setEditingPost(null)}
        onUpdated={handlePostUpdated}
      />

      <ConfirmDialog
        open={Boolean(postPendingDelete)}
        title="Excluir publicação"
        message="Tem certeza de que deseja excluir esta publicação? Os comentários também serão removidos."
        confirmLabel="Excluir"
        busy={deletingPost}
        busyLabel="Excluindo..."
        danger
        onConfirm={handleConfirmDeletePost}
        onCancel={() => {
          if (!deletingPost) setPostPendingDelete(null);
        }}
      />
    </div>
  );
}

export default Feed;
